import type {FC, MouseEvent} from 'react';
import {useState} from 'react';
// web has no filter-button wrapper; ButtonBase gives full control over the chip look
import {ButtonBase, Checkbox} from '@mui/material';
import {
  Box,
  Divider,
  Icon,
  Menu,
  MenuItem,
  Typography,
} from '@wellstone-solutions/web';
import type {ProgramOption} from '../../adapters';

type FilterButtonProps = {
  icon: string;
  label: string;
  active: boolean;
  expanded: boolean;
  onClick: (event: MouseEvent<HTMLElement>) => void;
};

// 32px pill with a leading icon, label and caret; purple-tinted once filtered.
const FilterButton: FC<FilterButtonProps> = ({
  icon,
  label,
  active,
  expanded,
  onClick,
}) => (
  <ButtonBase
    aria-haspopup="true"
    aria-expanded={expanded}
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      height: 32,
      pl: '11px',
      pr: '12px',
      border: '1px solid',
      borderColor: active ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
      borderRadius: 1,
      fontSize: 12.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      bgcolor: active ? 'rgba(81, 45, 109, 0.08)' : 'background.paper',
      color: active ? 'primary.main' : 'text.primary',
      '&:hover': {bgcolor: active ? 'rgba(81, 45, 109, 0.12)' : 'action.hover'},
    }}>
    <Icon name={icon} size={16} />
    {label}
    <Icon name="NavArrowDown" size={16} />
  </ButtonBase>
);

const MENU_ORIGIN = {
  anchorOrigin: {horizontal: 'left', vertical: 'bottom'} as const,
  transformOrigin: {horizontal: 'left', vertical: 'top'} as const,
};

// muiSkipListHighlight: MenuList skips this heading when picking initial focus
const GroupHeading: FC<{children: string; muiSkipListHighlight?: boolean}> = ({
  children,
}) => (
  <Typography
    sx={{
      px: 2,
      py: 0.75,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      color: 'text.secondary',
    }}>
    {children}
  </Typography>
);

// Forward MenuList's injected autoFocus/tabIndex; role/aria-checked expose
// selection (the Checkbox is visual only).
const CheckItem: FC<{
  label: string;
  checked: boolean;
  onClick: () => void;
  autoFocus?: boolean;
  tabIndex?: number;
}> = ({label, checked, onClick, autoFocus, tabIndex}) => (
  <MenuItem
    role="menuitemcheckbox"
    aria-checked={checked}
    autoFocus={autoFocus}
    tabIndex={tabIndex}
    onClick={onClick}
    sx={{fontSize: 14, minWidth: 240}}>
    <Checkbox
      checked={checked}
      size="small"
      tabIndex={-1}
      aria-hidden
      sx={{mr: 1, p: 0}}
    />
    {label}
  </MenuItem>
);

type SignalTypeFilterProps = {
  // Default-feed ("Changes in activity") kinds — multi-select, driven by `kind=`.
  changeOptions: ProgramOption[];
  selectedKinds: string[];
  onToggleKind: (slug: string) => void;
  // "Not using the app" access scopes — single-exclusive, driven by `scope=`.
  accessOptions: ProgramOption[];
  selectedScope: string | undefined;
  onToggleScope: (scope: string) => void;
  onClear: () => void;
};

export const SignalTypeFilter: FC<SignalTypeFilterProps> = ({
  changeOptions,
  selectedKinds,
  onToggleKind,
  accessOptions,
  selectedScope,
  onToggleScope,
  onClear,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const count = selectedKinds.length + (selectedScope ? 1 : 0);
  const label = count ? `Signal type · ${count}` : 'Signal type';

  return (
    <Box sx={{position: 'relative'}}>
      <FilterButton
        icon="FilterList"
        label={label}
        active={count > 0}
        expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        {...MENU_ORIGIN}>
        {accessOptions.length > 0 && (
          <GroupHeading muiSkipListHighlight>Not using the app</GroupHeading>
        )}
        {accessOptions.map((option) => (
          <CheckItem
            key={option.value}
            label={option.label}
            checked={selectedScope === option.value}
            onClick={() => onToggleScope(option.value)}
          />
        ))}
        <GroupHeading muiSkipListHighlight>Changes in activity</GroupHeading>
        {changeOptions.length === 0 && (
          <MenuItem disabled sx={{fontSize: 14}}>
            No signal types
          </MenuItem>
        )}
        {changeOptions.map((option) => (
          <CheckItem
            key={option.value}
            label={option.label}
            checked={selectedKinds.includes(option.value)}
            onClick={() => onToggleKind(option.value)}
          />
        ))}
        {count > 0 && <Divider />}
        {count > 0 && (
          <MenuItem
            onClick={onClear}
            sx={{fontSize: 13, color: 'primary.main'}}>
            Clear selection
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

type ProgramFilterProps = {
  options: ProgramOption[];
  // 'all' or a program id — the API only filters by a single program.
  selected: string;
  onChange: (value: string) => void;
};

const Tick: FC<{on: boolean}> = ({on}) => (
  <Box
    component="span"
    sx={{width: 16, mr: 1, display: 'inline-flex', color: 'primary.main'}}>
    {on && <Icon name="Check" size={16} />}
  </Box>
);

export const ProgramFilter: FC<ProgramFilterProps> = ({
  options,
  selected,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const current = options.find((option) => option.value === selected);

  const pick = (value: string) => {
    onChange(value);
    setAnchorEl(null);
  };

  return (
    <Box sx={{position: 'relative'}}>
      <FilterButton
        icon="Community"
        label={current ? current.label : 'Program'}
        active={selected !== 'all'}
        expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        {...MENU_ORIGIN}>
        <MenuItem
          onClick={() => pick('all')}
          sx={{fontSize: 14, minWidth: 200}}>
          <Tick on={selected === 'all'} />
          All programs
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => pick(option.value)}
            sx={{fontSize: 14, minWidth: 200}}>
            <Tick on={selected === option.value} />
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
